import React, { useState } from 'react';
import ContentCard from '../../components/structure/ContentCard';
import DetailLabel from '../../components/general/DetailLabel';
import PageContainer from '../../components/structure/PageContainer';
import FormFieldsContainer from '../../components/structure/FormFieldsContainer';
import TitleTextField from '../../components/forms/TitleTextField';
import FollowUpWithRepromptForm, { FollowUpWithRepromptFormData, FollowUpWithRepromptFormProps } from '../followUpForm';
import ConversationItemFormProps from '../../models/features/ConversationItemFormProps';
import ConversationFlow from '../conversationFlow';
import Loader from '../../components/general/Loader';
import SimpleChoiceContainer from '../../state/containers/SimpleChoiceContainer';
import SimpleChoiceModel from '../../models/features/api/SimpleChoices/SimpleChoiceModel';
import ConversationRequest from '../../models/conversationFlow/ConversationRequest';
import SimpleChoiceType from '../../models/features/api/SimpleChoices/SimpleChoiceType';
import RadioGroup, { RadioOption } from '../../components/forms/RadioGroup';
import IResult from '../../models/result/IResult';
import { SimpleChoiceFeatureTypeId } from '../../constants/featureTypeIds';
import ContentLanguageMultiSelector from '../../components/forms/ContentLanguageMultiSelector';
import { TreeItem } from '../../components/structure/NestedTree/NestedTree';
import ContentPagePanel from '../contentPage';
import RelatedContentMenu from '../relatedContentMenu';
import ScrollablePanelBody from '../../components/structure/ScrollablePanelBody';
import PanelTitle from '../contentPage/components/PanelTitle';
import ContentCollapsingSection from '../../components/structure/ContentCollapsingSection';
import ContentSectionTitle from '../contentPage/components/ContentSectionTitle';
import { getFeatureTypeIcon } from '../../models/extensions';
import PageError from '../../components/general/PageError';
import ConversationItemActionButtonToolbar from './components/ConversationItemActionButtonToolbar';
import TemplateLockedBanner from '../../components/general/TemplateLockedBanner';
import ConversationItemFieldSet from '../../components/forms/ConversationItemFieldSet';
import ContentInfoOptions from '../../components/structure/ContentInfoOptions';
import { WebhookChainingType } from '../../models/webhooks/api/WebhookChainingType';
import SimpleChoiceResponseModel from '../../models/features/api/SimpleChoices/SimpleChoiceResponseModel';
import { ContentItemPageState } from './BaseConversationItemForm';
import ResponseGroup from '../../models/features/ResponseGroup';
import ResponsesManager from '../responsesManager';
import { DirtyFormPrompt } from './components/DirtyFormPrompt';
import SensitiveDataOptions from '../contentPage/components/SensitiveDataOptions';
import DisableForFeatureFlagOptions from '../contentPage/components/DisableForFeatureFlagOptions';
import ContentItemTestCaseViewer from './ContentItemTestCaseViewer';

const userIcon = require('../../content/images/content-tree-icons/content-level/end-user-avatar.svg');
const contentInfoIcon = require('../../content/images/content-tree-icons/content-level/task-list-pin.svg');
const inputGroupIcon = require('../../content/images/content-tree-icons/content-level/input-stack.svg');

export interface SimpleChoiceFormData extends FollowUpWithRepromptFormData {
    title?: string
    choiceType?: SimpleChoiceType
    webhookChainingType?: WebhookChainingType
    responses: SimpleChoiceResponseModel[]
}
export interface SimpleChoiceFormProps extends FollowUpWithRepromptFormProps<SimpleChoiceModel>, ConversationItemFormProps<SimpleChoiceModel> {
    stateContainer: SimpleChoiceContainer
    simpleChoice?: SimpleChoiceModel
}

class SimpleChoiceForm extends FollowUpWithRepromptForm<SimpleChoiceModel, SimpleChoiceFormProps, SimpleChoiceFormData, ContentItemPageState> {

    componentDidUpdate(
        prevProps: SimpleChoiceFormProps,
        prevState: ContentItemPageState
    ) {
        super.componentDidUpdate(prevProps, prevState)
    }

    handleResponseIdFromSim = (id: string) => {
        this.setState({ simulatedResponseId: id })
    }

    handleAsyncSubmit(onSuccess: (result: IResult<SimpleChoiceModel>) => void) {
        if (this.props.onSubmit && this.manualValidate()) {
            return this.props.onSubmit(this.props.stateContainer.mapFormDataToModel(this.props.simpleChoice, this.props.values), onSuccess);
        }
    }
    handleDiscardChanges() {
        this.handleFinish();
    }
    handleDelete() {
        const confirmed = confirm("Are you sure you want to delete this?");
        if (confirmed) {
            this.props.stateContainer.delete(this.props.simpleChoice.id).then(() => {
                this.handleFinish();
            });
        }
    }

    handleCopy() {
        this.props.onSubmit(this.props.stateContainer.mapFormDataToModel(this.props.simpleChoice, this.props.values), () => {
            this.props.stateContainer.copy(this.props.simpleChoice.id).then(r2 => {
                if (r2.resultType == "Ok") {
                    this.props.history.push(`/v/${r2.data.applicationFeatureId}/simplechoices/${r2.data.id}`);
                }
            });
        });
    }
    getUserUtterances(): ConversationRequest[] {
        switch (this.props.values.choiceType) {
            case "Yes": return [
                { id: "1", content: "Yes", highlights: [] },
                { id: "2", content: "Yeah", highlights: [] },
                { id: "3", content: "Sure", highlights: [] },
                { id: "4", content: "Yes please", highlights: [] },
                { id: "5", content: "Uh huh", highlights: [] },
            ];
            case "No": return [
                { id: "1", content: "No", highlights: [] },
                { id: "2", content: "Nope", highlights: [] },
                { id: "3", content: "Nah", highlights: [] },
                { id: "4", content: "No thanks", highlights: [] },
                { id: "5", content: "Nuh uh", highlights: [] },
            ];
        }

        return []
    }
    handleOptionChange(option: RadioOption) {
        this.props.setFieldValue("choiceType", option.value);
    }
    getMenuTreeRoots() {
        var roots: TreeItem[] = [this.getContentInfoTreeItem(), {
            id: 'user-says',
            title: 'User-Says',
            isExpanded: this.isItemExpanded('user-says'),
            canExpand: true,
            icon: userIcon,
            linkTo: "#userSays",
            children: [{
                id: 'phrases',
                title: 'Phrases',
                canExpand: false,
                icon: inputGroupIcon,
                linkTo: "#Phrases",
            }]
        },
        ...this.getResponsesTreeItems(this.props.values.responses)]
        return roots;
    }
    getContentItem() {
        return this.props.simpleChoice;
    }
    handleContentItemOptionSelected(option: string, event: any) {
        event.stopPropagation();
        if (option == "Manage Webhooks") {
            this.showWebhookInstanceManager();
        }
        else if (option == "Duplicate") {
            this.handleCopy();
        }
        else if (option == "Delete") {
            this.handleDelete();
        }
    }
    handleResponsesChange(responseGroups: ResponseGroup[]) {
        this.props.setFieldValue("responses", this.getResponsesFromGroups(responseGroups));
    }
    render() {
        const props = { ...this.props };
        const isLoading = props.stateContainer.state.isSaving;
        const isDisabled = isLoading || this.props.isTemplateOwned;
        const responseGroups = this.getResponseGroups(this.props.values.responses);
        const { simulatedResponseId } = this.state
        if (this.props.responseStateContainer.state.isLoading || !this.props.application) {
            return <Loader />
        } return (
            <>
                <form onSubmit={props.handleSubmit}>
                    <DirtyFormPrompt allowPrompt={props.status?.submitting != true} />
                    <ContentPagePanel
                        relatedContentRender={() => (<RelatedContentMenu
                            parentContentId={this.props.previousContentId}
                            conversationItem={{ ...this.props.simpleChoice, featureTypeId: SimpleChoiceFeatureTypeId }}
                            history={this.props.history}
                            featureTypeId={SimpleChoiceFeatureTypeId}
                            followUp={responseGroups?.[0]?.firstResponse?.followUp}
                            contentId={this.props.simpleChoice?.id}
                            onClose={this.props.onToggleNavigation}
                            onSave={this.handleAsyncSubmit.bind(this)} />)
                        }
                        treeRender={this.renderTree.bind(this)}
                        formPanelRender={() => (
                            <PageContainer fill>
                                <ConversationItemActionButtonToolbar
                                    errors={Object.values(props.errors)}
                                    validateForm={props.validateForm}
                                    publishDisabled={this.props.assistantApplication == null}
                                    saveDisabled={Object.keys(props.errors).length > 0}
                                    contentItem={props.simpleChoice}
                                    isLoading={isLoading}
                                    handleDiscardChanges={this.handleDiscardChanges.bind(this)}
                                    handlePublishing={this.handleSaveAndPublish.bind(this)}
                                    submitForm={props.submitForm}
                                />
                                {props.simpleChoice?.createdFromId && <TemplateLockedBanner isLoading={props.stateContainer.state.isTogglingSync} isLinked={props.isTemplateOwned} onRelink={this.handleToggleRelinkModal.bind(this)} onUnlink={() => this.props.onToggleSync(this.props.simpleChoice.id, true)} />}

                                <ConversationItemFieldSet disabled={isDisabled} withBanner={props.simpleChoice?.createdFromId != null}>
                                    <ScrollablePanelBody className="scrollable-body">
                                        <PanelTitle title="Content Editing"
                                            detail="Here’s where you can fill in the info about this content and determine the course of the conversation by filling in the relevant details.">
                                        </PanelTitle>
                                        <ContentCollapsingSection title="Content Info" id="contentInfo" icon={contentInfoIcon} titleRightRowComponent={
                                            <ContentInfoOptions webhookCount={this.props.stateContainer.state.webhookUpdates?.webhookInstances?.length ?? this.props.stateContainer.state.attachedWebhooks?.length ?? 0}
                                                options={this.getOptions(this.props.simpleChoice != null)}
                                                optionSelected={this.handleContentItemOptionSelected.bind(this)}
                                                manageWebhooks={this.showWebhookInstanceManager.bind(this)}
                                                showSensitiveConversation={this.state.showSensitiveConversation}
                                                toggleSensitiveConversation={() => { this.toggleSensitiveConversation(); }}
                                                showDisabledForFeatures={this.state.showDisabledForFeatureFlags}
                                                toggleDisabledForFeatures={() => { this.toggleDisableForFeatureFlags(); }}
                                                flagsSelected={props.values.disabledForFeatureFlagIds?.length}
                                            />
                                        }>
                                            {this.state.showSensitiveConversation &&
                                                <SensitiveDataOptions
                                                    selectedOption={props.values.sensitiveConversationChange ?? "None"}
                                                    onChange={(value) => { props.setFieldValue("sensitiveConversationChange", value) }}
                                                />
                                            }
                                            {this.state.showDisabledForFeatureFlags &&
                                                <DisableForFeatureFlagOptions
                                                    appContainer={this.props.appContainer}
                                                    onChange={(value) => { props.setFieldValue("disabledForFeatureFlagIds", value) }}
                                                    selectedFeatureFlags={props.values.disabledForFeatureFlagIds}
                                                />
                                            }
                                            <ContentSectionTitle title="Simple Choice Conversation" icon={getFeatureTypeIcon(SimpleChoiceFeatureTypeId, true)} />
                                            <TitleTextField id="contentTitle" disabled={isDisabled} name="title" placeholder="For display only" value={props.values.title} label="Title" onChange={props.handleChange} onBlur={props.handleBlur} />
                                            <ContentLanguageMultiSelector id="contentLanguage" disabled={isDisabled} label="Language / Region" languages={props.application.languages} selectedLanguageIds={props.values.languages.map(l => l.id)} onChange={this.handleLanguagesChange.bind(this)} />
                                            {this.renderOptions(true, isDisabled)}
                                        </ContentCollapsingSection>
                                        <ContentCollapsingSection title="User Says" icon={userIcon} id="userSays">
                                            <ContentCard title="Phrases" icon={inputGroupIcon} id="Phrases">
                                                <FormFieldsContainer>
                                                    <DetailLabel text="Choose what type of response this belongs to" />
                                                    <RadioGroup onChange={this.handleOptionChange.bind(this)}
                                                        value={this.props.values.choiceType}
                                                        disabled={isDisabled}
                                                        options={[
                                                            { value: "Yes", label: "Yes" },
                                                            { value: "No", label: "No" }
                                                        ]} />
                                                </FormFieldsContainer>
                                            </ContentCard>

                                        </ContentCollapsingSection>
                                        <ResponsesManager applicationId={this.props.application.id}
                                            applicationFeatureId={this.props.applicationFeatureId}
                                            languages={this.props.values.languages}
                                            responseGroups={responseGroups}
                                            getConditionsKey={(c) => this.getConditionsKey(c)}
                                            showError={this.showError.bind(this)}
                                            isDisabled={isDisabled}
                                            onChange={this.handleResponsesChange.bind(this)}
                                            onBlur={this.props.handleBlur}
                                            simulatedResponseId={simulatedResponseId}
                                            responseIdToNamesRef={this.responseIdToNamesRef}
                                            contentItemTitle={this.props.values.title} />
                                    </ScrollablePanelBody>
                                </ConversationItemFieldSet>
                                <PageError errors={this.props?.stateContainer?.state?.errors ?? []} onClear={this.handleClearError.bind(this)} />
                            </PageContainer>
                        )}
                        testCasesRender={() => (
                            <ContentItemTestCaseViewer application={this.props.application} contentItemId={this.props.simpleChoice?.id} getResponseId={this.handleResponseIdFromSim} responseIdToNamesRef={this.responseIdToNamesRef} />
                        )}>
                    </ContentPagePanel>
                </form>
                {this.renderRelinkSyncWarning()}
                {this.renderWebhookInstanceManager(this.props.application.id, this.props.simpleChoice?.id, SimpleChoiceFeatureTypeId, this.props.values.webhookChainingType)}
            </>
        )
    }
}

export default SimpleChoiceForm;